import { useCallback, useEffect, useRef, useState } from "react";
import { requester } from "api/pesabackend";

export const DATE_FILTERS = {
  today: "today",
  yesterday: "yesterday",
  this_month: "this_month",
  this_year: "this_year",
  custom: "custom",
};

export const buildPaginatedListUrl = (path, {
  branchId,
  branchParam = "business_branch_id",
  dateFilter = DATE_FILTERS.this_month,
  startDate,
  endDate,
  page = 1,
  pageSize = 50,
  extraParams = {},
}) => {
  const params = new URLSearchParams();
  params.set(branchParam, branchId);
  params.set("date_filter", dateFilter);
  if (dateFilter === DATE_FILTERS.custom) {
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
  }
  params.set("page", String(page));
  params.set("page_size", String(pageSize));
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== false && value !== "") {
      params.set(key, String(value));
    }
  });
  return `${path}?${params.toString()}`;
};

export const parseNextUrl = (next) => {
  if (!next) return null;
  try {
    const url = new URL(next);
    const path = url.pathname.replace(/^\/api/, "") + url.search;
    return path;
  } catch {
    return next.startsWith("/") ? next : null;
  }
};

const usePaginatedList = ({
  path,
  branchId,
  branchParam = "business_branch_id",
  pageSize = 50,
  extraParams = {},
  externalRefresh,
}) => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.this_month);
  const [customRange, setCustomRange] = useState({ startDate: "", endDate: "" });
  const [refreshToken, setRefreshToken] = useState(0);

  const appliedCustomRange = useRef({ startDate: "", endDate: "" });
  const extraParamsKey = JSON.stringify(extraParams);

  const applyResponse = useCallback((response, append) => {
    const payload = response.data;
    const newRows = payload.data ?? [];
    setRows((prev) => (append ? [...prev, ...newRows] : newRows));
    setCount(payload.count ?? 0);
    setNextUrl(parseNextUrl(payload.next));
    setError(false);
  }, []);

  const fetchPage = useCallback(async (url, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const response = await requester.get(url);
      applyResponse(response, append);
    } catch {
      setError(true);
      if (!append) setRows([]);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [applyResponse]);

  useEffect(() => {
    if (!branchId) return;

    const startDate = dateFilter === DATE_FILTERS.custom ? appliedCustomRange.current.startDate : undefined;
    const endDate = dateFilter === DATE_FILTERS.custom ? appliedCustomRange.current.endDate : undefined;

    if (dateFilter === DATE_FILTERS.custom && (!startDate || !endDate)) return;

    const url = buildPaginatedListUrl(path, {
      branchId,
      branchParam,
      dateFilter,
      startDate,
      endDate,
      page: 1,
      pageSize,
      extraParams,
    });

    fetchPage(url, false);
    // extraParamsKey serializes extraParams to avoid unstable object reference loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, branchParam, dateFilter, path, pageSize, extraParamsKey, refreshToken, externalRefresh, fetchPage]);

  const loadMore = useCallback(() => {
    if (!nextUrl || loadingMore) return;
    fetchPage(nextUrl, true);
  }, [nextUrl, loadingMore, fetchPage]);

  const refresh = useCallback(() => {
    setRefreshToken((t) => t + 1);
  }, []);

  const applyCustomRange = useCallback(({ startDate, endDate }) => {
    appliedCustomRange.current = { startDate, endDate };
    setCustomRange({ startDate, endDate });
    setDateFilter(DATE_FILTERS.custom);
  }, []);

  return {
    rows,
    count,
    loading,
    loadingMore,
    error,
    hasMore: Boolean(nextUrl),
    loadMore,
    dateFilter,
    setDateFilter,
    customRange,
    setCustomRange,
    applyCustomRange,
    refresh,
  };
};

export default usePaginatedList;
